import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  ArrowBack,
  QrCode2,
  Download,
  CheckCircle,
  Cancel,
  TableRestaurant,
  Circle,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import { TableService } from '../services/tableService.js';
import { TableSessionService } from '../services/tableSessionService.js';
import apiClient, { extractData } from '../services/api.js';

export const TablesPage = () => {
  const navigate = useNavigate();
  const { outletId, outletName, accessType } = useAuth();
  const [tables, setTables] = useState([]);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [outletSlug, setOutletSlug] = useState('');
  const pollRef = useRef(null);
  const qrRef = useRef(null);

  const fetchTables = useCallback(async () => {
    if (!outletId) return;
    try {
      const data = await TableService.getTables(outletId);
      setTables(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  const fetchPendingSessions = useCallback(async () => {
    if (!outletId) return;
    try {
      const data = await TableSessionService.getPendingSessions(outletId);
      setPendingSessions(data.sessions || []);
    } catch (err) {
      // Silently fail on polling
    }
  }, [outletId]);

  // Fetch outlet slug
  useEffect(() => {
    const fetchSlug = async () => {
      try {
        const response = await apiClient.get(`/outlets/${outletId}`);
        const data = extractData(response);
        setOutletSlug(data.public_slug || '');
      } catch (err) {
        console.error('Error fetching outlet slug:', err);
      }
    };
    if (outletId) fetchSlug();
  }, [outletId]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Poll for pending sessions every 10s
  useEffect(() => {
    fetchPendingSessions();
    pollRef.current = setInterval(fetchPendingSessions, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchPendingSessions]);

  const handleAddTable = async () => {
    try {
      await TableService.createTable(outletId);
      fetchTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteTable = async (tableId) => {
    try {
      await TableService.deleteTable(tableId);
      fetchTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleFreeTable = async (tableId) => {
    try {
      await TableService.freeTable(tableId);
      fetchTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApproveSession = async (sessionId) => {
    try {
      await TableSessionService.approveSession(sessionId);
      fetchPendingSessions();
      fetchTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRejectSession = async (sessionId) => {
    try {
      await TableSessionService.rejectSession(sessionId);
      fetchPendingSessions();
      fetchTables();
    } catch (err) {
      setError(err.message);
    }
  };

  const generateQRUrl = (tableNumber) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/cust/${outletSlug}/${tableNumber}`;
  };

  const handleDownloadQR = (tableNumber) => {
    setSelectedTable(tableNumber);
    setQrDialogOpen(true);
  };

  const handleSaveQR = () => {
    const svgElement = qrRef.current?.querySelector('svg');
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 300;
    const padding = 30;
    const textAreaHeight = 60;
    canvas.width = size + padding * 2;
    canvas.height = size + padding * 2 + textAreaHeight;

    const img = new Image();
    img.onload = () => {
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw QR code
      ctx.drawImage(img, padding, padding, size, size);

      // Table number
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Table ${selectedTable}`, canvas.width / 2, size + padding + 28);

      // Outlet name
      ctx.font = '13px sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText(outletName || 'Restaurant', canvas.width / 2, size + padding + 50);

      const link = document.createElement('a');
      link.download = `table-${selectedTable}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#EBEBEF', p: 2, gap: 2 }}>
      {/* ─── Main Panel ─── */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.06)',
          p: 3,
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
          <IconButton onClick={() => navigate('/home')} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight={700}>
            Table Management
          </Typography>
          {accessType === 'owner' && (
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={handleAddTable}
              sx={{ ml: 'auto', borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}
            >
              Add Table
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!outletSlug && accessType === 'owner' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No public slug generated for this outlet. QR codes won't work until the backend generates one.
          </Alert>
        )}

        <Grid container spacing={2}>
          {tables.map((table) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={table.id}>
              <Card
                sx={{
                  borderRadius: '16px',
                  border: `2px solid ${table.status === 'occupied' ? '#FF9800' : '#4CAF50'}`,
                  textAlign: 'center',
                  position: 'relative',
                }}
              >
                <CardContent sx={{ pb: '8px !important', pt: 2 }}>
                  <TableRestaurant sx={{ fontSize: 32, color: table.status === 'occupied' ? '#FF9800' : '#4CAF50', mb: 1 }} />
                  <Typography fontWeight={700} fontSize="1rem">
                    Table {table.table_number}
                  </Typography>
                  <Chip
                    size="small"
                    icon={<Circle sx={{ fontSize: '8px !important' }} />}
                    label={table.status === 'occupied' ? 'Occupied' : 'Free'}
                    color={table.status === 'occupied' ? 'warning' : 'success'}
                    sx={{ mt: 0.5, fontSize: '0.7rem' }}
                  />
                  {table.current_session && (
                    <Typography fontSize="0.7rem" color="text.secondary" sx={{ mt: 0.5 }}>
                      {table.current_session.customer_name}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                    {outletSlug && (
                      <IconButton size="small" onClick={() => handleDownloadQR(table.table_number)} title="Download QR">
                        <QrCode2 fontSize="small" />
                      </IconButton>
                    )}
                    {table.status === 'occupied' && (
                      <IconButton size="small" onClick={() => handleFreeTable(table.id)} title="Free Table" color="warning">
                        <Cancel fontSize="small" />
                      </IconButton>
                    )}
                    {accessType === 'owner' && table.status === 'free' && (
                      <IconButton size="small" onClick={() => handleDeleteTable(table.id)} title="Remove Table" color="error">
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {tables.length === 0 && (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 6 }}>
            No tables added yet. Click "Add Table" to get started.
          </Typography>
        )}
      </Box>

      {/* ─── Right Panel: Pending Requests ─── */}
      <Box
        sx={{
          width: 320,
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.06)',
          p: 2,
          overflow: 'auto',
        }}
      >
        <Typography fontWeight={700} fontSize="1rem" sx={{ mb: 2 }}>
          <Badge badgeContent={pendingSessions.length} color="error" sx={{ mr: 1 }}>
            Pending Requests
          </Badge>
        </Typography>

        {pendingSessions.length === 0 ? (
          <Typography color="text.secondary" fontSize="0.85rem" textAlign="center" sx={{ py: 4 }}>
            No pending table requests
          </Typography>
        ) : (
          pendingSessions.map((session) => (
            <Card key={session.session_id} sx={{ mb: 1.5, borderRadius: '12px', border: '1px solid rgba(255,152,0,0.3)' }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography fontWeight={700} fontSize="0.85rem">
                  Table {session.table_number}
                </Typography>
                <Typography fontSize="0.8rem" color="text.secondary">
                  Customer: {session.customer_name}
                </Typography>
                <Typography fontSize="0.7rem" color="text.disabled">
                  {new Date(session.created_at).toLocaleTimeString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApproveSession(session.session_id)}
                    sx={{ borderRadius: '8px', textTransform: 'none', flex: 1, fontSize: '0.75rem' }}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleRejectSession(session.session_id)}
                    sx={{ borderRadius: '8px', textTransform: 'none', flex: 1, fontSize: '0.75rem' }}
                  >
                    Reject
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* ═══ QR Code Dialog ═══ */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs">
        <DialogTitle>QR Code — Table {selectedTable}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
          <Box
            ref={qrRef}
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: 3,
              bgcolor: '#FFFFFF',
              borderRadius: '16px',
              border: '2px solid #F2F2F7',
            }}
          >
            {selectedTable && (
              <QRCodeSVG
                value={generateQRUrl(selectedTable)}
                size={220}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#1C1C1E"
                style={{ display: 'block' }}
              />
            )}
            <Typography fontWeight={700} fontSize="1rem" sx={{ mt: 2 }}>
              Table {selectedTable}
            </Typography>
            <Typography fontSize="0.8rem" color="text.secondary">
              {outletName || 'Restaurant'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<Download />} onClick={handleSaveQR}>
            Download PNG
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
